<template>
    <AppFormValidator
        ref="validator"
        #default="{ errors }"
        :model="model"
        :rules="rules"
    >
        <CForm @submit.prevent>
            <CInput
                v-model="model.title"
                horizontal
                label="Название"
                placeholder="Введите название"
                :is-valid="errors.title && errors.title.isValid"
            />

            <CTextarea
                v-model="model.description"
                :rows="5"
                horizontal
                label="Краткое описание"
                placeholder="Укажите краткое описание"
            />

            <CInput
                v-model="model.quantity"
                type="number"
                horizontal
                label="Количество (шт)"
            />

            <CInput
                v-model="model.price"
                :is-valid="errors.price && errors.price.isValid"
                type="number"
                horizontal
                label="Цена"
            />

            <CSelect
                :value.sync="model.vat"
                :horizontal="true"
                :options="vatOptions"
                label="Значения ставки НДС"
                placeholder="Выберите значения ставки НДС"
            />

            <CInput
                v-model="model.weight_step"
                type="number"
                horizontal
                label="Шаг для добавления в корзину"
                :is-valid="errors.weight_step && errors.weight_step.isValid"
            />

            <CSelect
                :value.sync="model.weight_unit"
                :horizontal="true"
                :options="weightUnits"
                label="Единицы измерения"
                placeholder="Выберите единицы измерения"
            />

            <CInput
                v-model="model.weight"
                :is-valid="errors.weight && errors.weight.isValid"
                type="number"
                horizontal
                label="Вес"
            />

            <CSelect
                :value.sync="model.cert_required"
                :horizontal="true"
                :options="certRequiredOptions"
                label="Сертификация"
                placeholder="Выберите статус сертификации"
            />

            <CTextarea
                v-model="certUrlsText"
                :rows="3"
                horizontal
                label="Ссылки на сертификаты"
                placeholder="Одна или несколько ссылок, каждая с новой строки"
            />

            <CInput
                v-model="model.position"
                :is-valid="errors.position && errors.position.isValid"
                horizontal
                label="Позиция"
            />

            <div id="product-picture" class="flex items-start mt-2">
                <div class="w-1/4 col-form-label">Картинки</div>
                <div class="flex-1">
                    <CInputFile
                        custom
                        placeholder="Выберите изображение"
                        :is-valid="errors.pictures && errors.pictures.isValid"
                        @change="uploadPicture"
                    />

                    <div class="relative flex flex-wrap w-full mt-2 -ml-2">
                        <div
                            v-for="item in model.pictures"
                            :key="item"
                            class="w-1/4 p-2">
                            <div
                                class="relative w-full overflow-hidden bg-no-repeat bg-cover rounded-md shadow-md group-hover"
                                :style="{
                                    paddingTop: '100%',
                                    backgroundImage: `url(${getStaticPath(item)})`,
                                    backgroundPosition: 'center center'
                                }"
                            >
                                <button
                                    class="absolute top-0 right-0 w-8 h-8 text-xl text-white bg-red-400 rounded-bl-md"
                                    @click="removePicture(item)"
                                >
                                    <CIcon :height="16" :content="cilTrash" />
                                </button>

                                <button
                                    class="absolute bottom-0 flex items-center w-full h-10 px-1 bg-black bg-opacity-75"
                                    @click="toggleMainPicture(item)"
                                >
                                    <div
                                        v-if="model.main_picture === item"
                                        class="flex items-center text-green-500"
                                    >
                                        <CIcon :height="20" :content="cilCheckCircle" />
                                        <span class="ml-2 text-xs">Основная картинка</span>
                                    </div>
                                    <div v-else class="flex items-center text-white">
                                        <CIcon :height="20" :content="cilCircle" />
                                        <span class="ml-2 text-xs">Выбрать как основную</span>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <CRow class="mt-4">
                <CCol
                    tag="label"
                    sm="3"
                    class="col-form-label">
                    Энергетик (18+)
                </CCol>
                <CCol sm="9">
                    <CSwitch
                        color="dark"
                        shape="pill"
                        variant="opposite"
                        :checked.sync="model.energetic"
                    />
                    <small class="form-text text-muted">
                        Отметьте, если товар является энергетиком или требует подтверждения возраста 18+
                    </small>
                </CCol>
            </CRow>
            <CRow class="mt-4">
                <CCol
                    tag="label"
                    sm="3"
                    class="col-form-label">
                    Популярный
                </CCol>
                <CCol sm="9">
                    <CSwitch
                        color="dark"
                        shape="pill"
                        variant="opposite"
                        :checked.sync="model.popular"
                    />
                </CCol>
            </CRow>
            <CRow>
                <CCol
                    tag="label"
                    sm="3"
                    class="col-form-label">
                    Приоритет в выдаче в популярном
                </CCol>
                <CCol sm="3">
                    <CInput
                        v-model="model.priority"
                        placeholder="Приоритет"
                    />
                </CCol>
            </CRow>
            <CRow>
                <CCol
                    tag="label"
                    sm="3"
                    class="col-form-label">
                    Активен
                </CCol>
                <CCol sm="9">
                    <CSwitch
                        color="dark"
                        shape="pill"
                        variant="opposite"
                        :checked.sync="model.enabled"
                    />
                </CCol>
            </CRow>
        </CForm>

        <div
            v-if="adminOnly || !entity || !entity.grocer || !entity.grocer.alias" 
            class="flex mt-4">
            <CButton
                color="primary"
                @click="submit"
            >{{ submitButtonTitle }}
            </CButton>
            <CButton
                v-if="model.id"
                variant="outline"
                color="danger"
                class="ml-4"
                @click="promptDelete"
            >Удалить
            </CButton>
        </div>

        <AppConfirmDialog
            ref="confirmDialog"
            title="Подтверждение"
            color="danger"
            text="Вы действительно хотите удалить запись? Это действие нельзя будет отменить."
            @confirm="confirmDelete"
        />
    </AppFormValidator>
</template>

<script>
    import to from 'await-to-js';
    import { morphism } from 'morphism';
    import { requiredRule } from '@/utils/rules';
    import { focusProductPicture } from '@/utils/focusPicture';
    import { cilTrash, cilCheckCircle, cilCircle } from '@coreui/icons';

    import AppFormValidator from '@/components/AppFormValidator';
    import AppConfirmDialog from '@/components/AppConfirmDialog';

    export default {
        components: {
            AppFormValidator,
            AppConfirmDialog,
        },

        props: {
            entity: {
                type: Object,
                default: () => ({}),
            },
        },

        data() {
            return {
                isAuthorized: false,
                cilTrash,
                cilCircle,
                cilCheckCircle,
                model: {},
                schema: {
                    id: 'id',
                    title: 'title',
                    description: 'description',
                    main_picture: 'main_picture',
                    price: 'price',
                    vat: 'vat',
                    quantity: 'quantity',
                    weight_unit: 'weight_unit',
                    weight_step: 'weight_step',
                    weight: 'weight',
                    priority: 'priority',
                    cert_required: source => source.cert_required === undefined ? null : source.cert_required,
                    cert_urls: source =>
                        Array.isArray(source.cert_urls) ? source.cert_urls : [],
                    grocer_id: () => this.$route.params.id,
                    position: source => (parseInt(source.position) ? source.position : 1),
                    pictures: source =>
                        Array.isArray(source.pictures) ? source.pictures : [],
                    popular: source => Boolean(source.popular),
                    enabled: source => Boolean(source.enabled),
                    energetic: source => Boolean(source.energetic),
                },
                rules: {
                    title: [requiredRule],
                    // description: [requiredRule],
                    main_picture: [requiredRule],
                    pictures: [requiredRule],
                    popular: [requiredRule],
                    position: [requiredRule],
                    enabled: [requiredRule],
                    price: [requiredRule],
                    weight_step: [requiredRule],
                    weight: [requiredRule],
                },
                vatOptions: [
                    { value: null, label: 'НДС не облагается' },
                    { value: 0, label: 'НДС 0%' },
                    { value: 5, label: 'НДС 5%' },
                    { value: 7, label: 'НДС 7%' },
                    { value: 10, label: 'НДС 10%' },
                    { value: 20, label: 'НДС 20%' },
                    { value: 22, label: 'НДС 22%' },
                    { value: 105, label: 'Расчетный НДС 5/105' },
                    { value: 107, label: 'Расчетный НДС 7/107' },
                    { value: 110, label: 'Расчетный НДС 10/110' },
                    { value: 120, label: 'Расчетный НДС 20/120' },
                    { value: 122, label: 'Расчетный НДС 22/122' },
                ],
                weightUnits: [
                    { value: 0, label: 'шт' },
                    { value: 11, label: 'кг' },
                ],
                certRequiredOptions: [
                    { value: null, label: 'Не указано' },
                    { value: true, label: 'Сертификация обязательна' },
                    { value: false, label: 'Не подлежит сертификации' },
                ]
            };
        },

        computed: {
            certUrlsText: {
                get() {
                    return Array.isArray(this.model.cert_urls) ? this.model.cert_urls.join('\n') : '';
                },
                set(value) {
                    this.model.cert_urls = this.parseCertUrls(value);
                }
            },
            adminOnly() {
                return !JSON.parse(localStorage.getItem('settings')).is_business_admin;
            },
            submitButtonTitle() {
                return typeof this.entity.id === 'number' ? 'Сохранить' : 'Создать';
            },
        },

        created() {
            this.setModel();
        },

        mounted() {
            focusProductPicture(this.$route);
        },

        methods: {
            setModel() {
                this.model = morphism(this.schema, this.entity);
            },

            async uploadPicture(FileList) {
                const form = new FormData();
                form.append('file', FileList[0]);

                const [err, result] = await to(this.axios.post('/files/upload', form));

                if (err) {
                    this.$root.$emit('showError', 'Ошибка. Не удалось загрузить картинку. ' + err);
                }

                this.model.pictures.push(result.data.filename);

                if (this.model.pictures.length === 1) {
                    this.toggleMainPicture(result.data.filename);
                }
            },

            async submit() {
                const [error] = await to(this.$refs.validator.validate());

                if (error) {
                    return false;
                }

                const grocer_id = this.$route.params.id;
                const { id, ...payload } = this.model;

                payload.energetic = !!payload.energetic;
                payload.popular = !!payload.popular;
                payload.enabled = !!payload.enabled;

                payload.position = parseInt(payload.position);
                payload.priority = parseInt(payload.priority);
                payload.price = parseFloat(payload.price);
                payload.quantity = parseInt(payload.quantity);
                payload.weight = parseInt(payload.weight);
                payload.weight_step = parseFloat(payload.weight_step);
                payload.cert_required = this.parseCertRequired(payload.cert_required);
                payload.cert_urls = this.parseCertUrls(payload.cert_urls);


                if (typeof id === 'number') {
                    const [err] = await to(this.axios.put(`/grocers/${grocer_id}/goods/${id}`, payload));
                    if (err) {
                        return this.$root.$emit('showError', 'Ошибка. Не удалось сохранить запись. ' + err);
                    }
                } else {
                    const [err, result] = await to(this.axios.post(`/grocers/${grocer_id}/goods`, payload));
                    if (err) {
                        return this.$root.$emit('showError', 'Ошибка. Не удалось создать новую запись. ' + err);
                    }
                    this.$router.push(`/grocers/${this.$route.params.id}/goods/${result.data.id}`);
                    return;
                }

                this.$router.push(`/grocers/${this.$route.params.id}/goods/${this.$route.params.goods_id}`);
            },

            promptDelete() {
                this.$refs.confirmDialog.open();
            },

            async confirmDelete() {
                if (!this.model.id) {
                    return false;
                }

                const [error] = await to(this.axios.delete(`/grocers/${this.model.grocer_id}/goods/${this.model.id}`))

                if (error) {
                    return this.$root.$emit('showError', 'Ошибка при удалении записи. ' + error);
                }

                this.$emit('deleted')
            },

            toggleMainPicture(url) {
                if (this.model.main_picture === url) {
                    return false;
                }

                this.model.main_picture = url;
            },

            removePicture(url) {
                const idx = this.model.pictures.indexOf(url);

                if (idx !== -1) {
                    this.model.pictures.splice(idx, 1);
                }
            },

            parseCertUrls(value) {
                if (!value) {
                    return [];
                }

                const rawItems = Array.isArray(value)
                    ? value
                    : String(value).match(/https?:\/\/[^\s\])>,;]+/g) || String(value).split(/[\n,;|]+/);

                return [...new Set(rawItems
                    .map(item => String(item).trim())
                    .filter(Boolean))];
            },

            parseCertRequired(value) {
                if (value === null || value === undefined || value === '') {
                    return null;
                }
                if (value === true || value === 'true' || value === 1 || value === '1') {
                    return true;
                }
                if (value === false || value === 'false' || value === 0 || value === '0') {
                    return false;
                }
                return null;
            },

            getStaticPath(filename = '') {
                if (filename.startsWith('http')) {
                    return filename;
                }

                return `${process.env.VUE_APP_STATIC_BASE_URL}/${filename}`;
            },
        },
    };
</script>
<style scoped lang="scss">

</style>
